import { useContext, useEffect, useState } from "react";
import {
  deleteFile,
  getFiles,
  getLogos,
  getTemplates,
} from "../http/chapterApi";
import FileUpload from "../icons/FileUpload.svg?react";
import { Chapter } from "../types/apiTypes";
import { DocumentFile } from "../types/types";
import { DataContext } from "./AppRouter";
import DocumentsListItem from "./DocumentsListItem";
import DocumentUpload from "./DocumentUpload";
import styles from "./List.module.css";
import ListHeader from "./ListHeader";
import ListItem from "./ListItem";
import Icon from "./ui/Icon";
import Loading from "./ui/Loading";

interface Props {
  isTemplates?: boolean;
  isDocuments?: boolean;
  items?: string[];
}

const filterTemplates = (
  templates: string[] | undefined,
  chapter: Chapter | undefined,
) =>
  templates?.filter(
    (template) =>
      !(
        template === "Einzahlung_Mehrwertsteuer_Vorlage.xltx" &&
        !chapter?.vatNumber
      ),
  );

const List = ({ isTemplates, isDocuments, items }: Props) => {
  const [documentFiles, setDocumentFiles] = useState<DocumentFile[]>();
  const [stringFiles, setStringFiles] = useState<string[]>();
  const { chapter } = useContext(DataContext);

  useEffect(() => {
    if (items) {
      setStringFiles(items);
      return;
    }

    if (!chapter && isDocuments) return;

    const fetchResponse = async () => {
      if (isDocuments && chapter) {
        const response = await getFiles(chapter.id);
        setDocumentFiles(response);
      } else if (isTemplates) {
        const response = await getTemplates();
        const filterResponse = filterTemplates(response, chapter);
        setTimeout(() => setStringFiles(filterResponse), 350);
      } else {
        const response = await getLogos(chapter);
        setTimeout(() => setStringFiles(response), 350);
      }
    };

    fetchResponse();
  }, [chapter, isTemplates, isDocuments, items]);

  if (!chapter && isDocuments) return null;

  const getEmptyStateText = () => {
    if (isDocuments) return "Keine Dokumente vorhanden";
    if (isTemplates) return "Keine Vorlagen vorhanden";
    if (items) return "Keine Newsletter vorhanden";
    return "Keine Logos vorhanden";
  };

  const getHeaderItems = () => {
    if (isDocuments) return ["NAME", "ZULETZT GEÄNDERT"];
    return ["NAME", "DATEITYP", undefined];
  };

  const isLoading = isDocuments ? !documentFiles : !stringFiles;
  const isEmpty = isDocuments ? !documentFiles?.length : !stringFiles?.length;

  if (isDocuments) {
    return (
      <>
        {chapter && (
          <DocumentUpload
            chapterId={chapter.id}
            files={documentFiles ?? []}
            onFileUploaded={(newFile) =>
              setDocumentFiles((prev) => [
                ...(prev ?? []).filter(({ id }) => id !== newFile.id),
                newFile,
              ])
            }
          />
        )}
        <table className={styles.table}>
          <thead>
            <ListHeader items={getHeaderItems()} wideLayout={true} />
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan={2}>
                  <Loading />
                </td>
              </tr>
            ) : isEmpty ? (
              <tr>
                <td colSpan={2} className={styles.container}>
                  <Icon className={styles["btn-icon"]} glyph={FileUpload} />
                  <p>{getEmptyStateText()}</p>
                </td>
              </tr>
            ) : (
              documentFiles &&
              chapter &&
              documentFiles.map((item) => (
                <DocumentsListItem
                  key={item.id}
                  file={item}
                  chapterId={chapter.id}
                  onDelete={(fileId) => {
                    deleteFile(chapter.id, fileId);
                    setDocumentFiles((files) =>
                      files?.filter((f) => f.id !== fileId),
                    );
                  }}
                />
              ))
            )}
          </tbody>
        </table>
      </>
    );
  }

  return (
    <table className={styles.table}>
      <thead>
        <ListHeader items={getHeaderItems()} wideLayout={true} />
      </thead>
      <tbody>
        {isLoading ? (
          <Loading />
        ) : isEmpty ? (
          <p>{getEmptyStateText()}</p>
        ) : (
          stringFiles?.map((item, index) => (
            <ListItem file={item} key={index} isTemplates={isTemplates} />
          ))
        )}
      </tbody>
    </table>
  );
};

export default List;
